import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BrowserPortInfo, WebSerialScaleService } from '../../services/web-serial-scale.service';

@Component({
  selector: 'app-change-scale-port',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './change-scale-port.component.html',
  styleUrls: ['./change-scale-port.component.css'],
})
export class ChangeScalePortComponent implements OnInit {
  availablePorts: BrowserPortInfo[] = [];
  selectedIdentity = '';
  loadingPorts: boolean = false;
  saving: boolean = false;

  /** True when running in Chrome/Edge — Web Serial API is available */
  readonly isWebSerialSupported: boolean;

  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private webSerialScaleService: WebSerialScaleService,
    public dialogRef: MatDialogRef<ChangeScalePortComponent>,
  ) 
  {
    this.isWebSerialSupported = this.webSerialScaleService.isSupported;
  }

  ngOnInit(): void {
    this.loadConfig();
  }

  loadConfig(): void {
    this.loadingPorts = true;
    this.webSerialScaleService.getGrantedPorts().then(ports => {
      this.availablePorts = ports;
      if (ports.length === 1) this.selectedIdentity = ports[0].identity;
    }).catch(() => this.errorMessage = 'Unable to enumerate browser serial devices.')
      .finally(() => this.loadingPorts = false);
  }

  async save(): Promise<void> {
    // After a successful selection message is shown, next OK closes the dialog.
    if (this.successMessage) {
      this.dialogRef.close();
      return;
    }

    this.successMessage = null;
    this.errorMessage = null;

    if (!this.selectedIdentity) {
      this.errorMessage = 'Select an authorized browser serial device.';
      return;
    }
    this.saving = true;
    try {
      const selected = await this.webSerialScaleService.selectGrantedPort(this.selectedIdentity);
      this.successMessage = `Scale selected: ${selected.label}.`;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Failed to select scale.';
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {    
    this.clearMessages();
    this.dialogRef.close();
  }

  /**
   * Opens the browser's native serial port picker so the user can grant access to a new port.
   * After selection the granted-ports list is refreshed automatically.
    * Web Serial does not expose COM names, so this grants browser permission but
    * does not map the saved COM name to the selected SerialPort handle.
   */
  async detectPorts(): Promise<void> {
    this.clearMessages();
    if (!this.isWebSerialSupported) return;
    try {
      const selected = await this.webSerialScaleService.authorizeAndSelectPort();
      this.availablePorts = await this.webSerialScaleService.getGrantedPorts();
      this.selectedIdentity = selected.identity;
    } catch (err: any) {
      // User cancelled the picker — not an error worth showing.
      if (err?.name !== 'NotFoundError') {
        this.errorMessage = err?.message || 'Could not detect serial ports.';
      }
    }
  }

  clearMessages(): void {
    this.successMessage = null;
    this.errorMessage = null;
  }
}
